import { AuthContext, camelize } from '@repo-level/core-front';
import type {
  Lootbox,
  PlayerBonus,
  PlayerCompoints,
  PlayerFreespin,
  PlayerTransaction,
  ReferralProgramRealTime,
} from 'api/common';
import { playerBonuses, playerFreespins, playerLootboxes } from 'api/common';
import { useSubscription } from 'contexts/centrifugo/useSubscription';
import { groupMemberActiveBonus } from 'helpers/user';
import type { FC } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import type { State } from './reducer';
import { ACTION_TYPE, reducer } from './reducer';

const MINUTE_IN_MILLISECONDS = 60_000;

const EMPTY_BONUSES: PlayerBonus[] = [];
const EMPTY_FREESPINS: PlayerFreespin[] = [];
const EMPTY_LOOTBOXES: Lootbox[] = [];

interface WSGroup {
  id: string;
  name: string;
  /** Events that put the player into the group or took them out of it */
  conditions: string[];
  writable: boolean;
  /** Whether the group is a status group — not a membership flag */
  status?: boolean;
}

/** `groups_updates` publishes the player's full group list, not a delta */
export type WSGroupsUpdate = WSGroup[];

interface RealTimeServiceContextData extends State {
  fetchLootboxes: () => void;
  fetchBonuses: () => void;
  resetReceivedBonus: () => void;
}

export const RealTimeServiceContext = createContext<RealTimeServiceContextData>(
  null as any
);

const initialState: State = {
  lootboxes: [],
  lootboxesPending: true,
  playerPayments: [],
  bonuses: [],
  bonusesPending: true,
  freespins: [],
  freespinsPending: true,
  receivedBonus: null,
  referralProgram: null,
  compPoints: null,
};

export const RealTimeServiceContextProvider: FC = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const {
    isAuthenticated,
    user,
    updateUserWallet,
    updateCompPoints,
    updateUser,
  } = useContext(AuthContext);

  const fetchLootboxes = useCallback(async () => {
    try {
      const { data: lootboxes } = await playerLootboxes();

      dispatch({
        type: ACTION_TYPE.SET_LOOTBOXES,
        payload: { lootboxes },
      });
    } catch {
      dispatch({ type: ACTION_TYPE.SET_LOOTBOXES_SETTLED });
    }
  }, []);

  useEffect(() => {
    if (user?.id == null) return;

    (async () => {
      try {
        await fetchLootboxes();
      } catch {}
    })();
  }, [user?.id, fetchLootboxes]);

  const fetchBonuses = useCallback(async () => {
    try {
      const [{ data: bonuses }, { data: freespins }] = await Promise.all([
        playerBonuses(),
        playerFreespins(),
      ]);

      dispatch({
        type: ACTION_TYPE.SET_FREESPINS,
        payload: { freespins },
      });

      dispatch({
        type: ACTION_TYPE.SET_BONUSES,
        payload: { bonuses },
      });
    } catch {}
  }, []);

  useSubscription<PlayerBonus>('bonuses_changes', (data) => {
    dispatch({
      type: ACTION_TYPE.UPDATE_BONUSES,
      payload: { bonus: camelize(data) as PlayerBonus },
    });

    updateUserWallet();
  });

  useSubscription<PlayerFreespin>('freespins_changes', (data) => {
    dispatch({
      type: ACTION_TYPE.UPDATE_FREESPINS,
      payload: { freespin: camelize(data) as PlayerFreespin },
    });
  });

  useSubscription<Lootbox>('lootboxes_changes', (data) => {
    dispatch({
      type: ACTION_TYPE.ADD_LOOTBOX,
      payload: { lootbox: camelize(data) as Lootbox },
    });
  });

  useSubscription<WSGroupsUpdate>('groups_updates', (data) => {
    const statuses = data.map(({ id, name }) => ({ id, name }));

    updateUser({ statuses });
  });

  useSubscription<PlayerTransaction>('payments_changes', (data) => {
    const payment = camelize(data) as PlayerTransaction;

    dispatch({
      type: ACTION_TYPE.ADD_PAYMENT,
      payload: { payment },
    });

    if (payment.success) updateUserWallet();
  });

  useSubscription<PlayerCompoints>('comps_award', (data) => {
    const compPoints = camelize(data) as PlayerCompoints;

    dispatch({
      type: ACTION_TYPE.SET_COMP_POINTS,
      payload: { compPoints },
    });

    updateCompPoints(compPoints);
  });

  useSubscription<ReferralProgramRealTime>(
    'referral_system_notifications',
    (data) => {
      dispatch({
        type: ACTION_TYPE.REFERRAL_PROGRAM,
        payload: {
          referralProgram: camelize(data) as ReferralProgramRealTime,
        },
      });
    }
  );

  const resetReceivedBonus = useCallback(() => {
    dispatch({ type: ACTION_TYPE.RESET_RECEIVED_BONUS });
  }, []);

  useEffect(() => {
    if (user?.id == null) {
      dispatch({ type: ACTION_TYPE.RESET_USER_DATA });
      return;
    }

    fetchBonuses();
  }, [user?.id, fetchBonuses]);

  useEffect(() => {
    if (user?.id == null || !groupMemberActiveBonus(user)) return;

    const fetchBonuses = async () => {
      try {
        const { data: bonuses } = await playerBonuses();

        dispatch({
          type: ACTION_TYPE.SET_BONUSES,
          payload: { bonuses },
        });
      } catch {}
    };

    const fetchBonusesInterval = setInterval(
      () => fetchBonuses(),
      MINUTE_IN_MILLISECONDS
    );

    return () => clearInterval(fetchBonusesInterval);
  }, [user]);

  const value = useMemo(
    () => ({
      ...state,
      bonuses: isAuthenticated ? state.bonuses : EMPTY_BONUSES,
      freespins: isAuthenticated ? state.freespins : EMPTY_FREESPINS,
      lootboxes: isAuthenticated ? state.lootboxes : EMPTY_LOOTBOXES,
      fetchLootboxes,
      fetchBonuses,
      resetReceivedBonus,
    }),
    [state, isAuthenticated, fetchLootboxes, fetchBonuses, resetReceivedBonus]
  );

  return (
    <RealTimeServiceContext.Provider value={value}>
      {children}
    </RealTimeServiceContext.Provider>
  );
};
